import { CHAIN_ID } from '@derivation-tech/context';
import { TickMath } from '@synfutures/sdks-perp';
import { BigNumber } from 'ethers';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FETCHING_STATUS } from '@/constants';
import { EARN_TYPE } from '@/constants/earn';
import { TABLE_TYPES } from '@/constants/global';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  IAddLiquidityFormState,
  IAddLiquiditySimulation,
  IRemoveLiquidityFormState,
  IRemoveLiquiditySimulation,
} from '@/types/earn';
import { ILiquidityFilters } from '@/types/liquidity';
import { getLeftRatio, getRightRatio } from '@/utils/pair';
import { getAdjustTradePrice } from '@/utils/pairs';

import { useUserAddr } from '@/hooks/web3/useChain';
import { TokenInfo } from '@/types/token';
import { useWrappedRangeList } from '../account/rangeHook';
import { useCombinedPairFromUrl } from '../pair/hook';
import { setIsAddLiquidityInputRisky } from './action';
import { useWhitelistConfig } from './query';
import {
  selectAddLiquidityFormStateByChainId,
  selectAddLiquidityFormStatusByChainId,
  selectAddLiquiditySimulation,
  selectChainPoolFilters,
  selectEarnFormType,
  selectEarnPortfolioTab,
  selectRemoveLiquidityFormStateByChainId,
  selectRemoveLiquidityFormStatusByChainId,
  selectRemoveLiquiditySimulation,
  selectRemoveLiquiditySuccess,
} from './slice';

export function useAddLiquidationSimulation(chainId: CHAIN_ID | undefined): IAddLiquiditySimulation | undefined {
  const simulation = useAppSelector(selectAddLiquiditySimulation(chainId));
  const { t } = useTranslation();
  const currentPair = useCombinedPairFromUrl(chainId);
  const addLiqState = useAddLiquidityFormState(chainId);
  const minMargin = useMemo(
    () => WrappedBigNumber.from(simulation?.data?.minMargin || 0),
    [simulation?.data?.minMargin],
  );

  return useMemo(() => {
    if (simulation) {
      return {
        ...simulation,
        message:
          simulation.message ||
          (minMargin.gt(addLiqState?.amount || '')
            ? t('common.earn.notEnoughMargin', {
                minAmount: minMargin.formatDisplayNumber(),
                // TODO get this from sdk
                minEffectiveLiquidity:
                  WrappedBigNumber.from(simulation?.data?.minEffectiveQuoteAmount || 0)?.formatDisplayNumber() || '',
                quote: currentPair?.rootInstrument.quoteToken.symbol,
              })
            : undefined),
      };
    }
    return simulation;
  }, [simulation, minMargin, addLiqState?.amount, t, currentPair?.rootInstrument.quoteToken.symbol]);
}
export function useAddLiquidationSimulationData(chainId: CHAIN_ID | undefined): IAddLiquiditySimulation | undefined {
  const simulation = useAppSelector(selectAddLiquiditySimulation(chainId));
  return useMemo(() => simulation, [simulation]);
}

export function useAddLiquidityFormState(chainId: CHAIN_ID | undefined): IAddLiquidityFormState | undefined {
  return useAppSelector(selectAddLiquidityFormStateByChainId(chainId));
}
export function useAddLiquidityFormStateStatus(chainId: CHAIN_ID | undefined): FETCHING_STATUS {
  return useAppSelector(selectAddLiquidityFormStatusByChainId(chainId));
}

export function useIsAddLiquidityInputRisky() {
  const isAddLiquidityInputRisky = useAppSelector((state) => {
    return state.earn.isAddLiquidityInputRisky;
  });
  const dispatch = useAppDispatch();
  const setIsAddLiquidityInputRiskyCb = useCallback(
    (isAddLiquidityInputRisky: boolean) => dispatch(setIsAddLiquidityInputRisky(isAddLiquidityInputRisky)),
    [dispatch],
  );
  return {
    isAddLiquidityInputRisky,
    setIsAddLiquidityInputRisky: setIsAddLiquidityInputRiskyCb,
  };
}

export function usePairPriceRange(
  alpha: BigNumber,
  curTick: number,
  isInverse: boolean | undefined,
): { prices: number[]; ticks: number[] } {
  return useMemo(() => {
    if (alpha) {
      const [min, max] = TickMath.getTickRangeByAlpha(alpha, curTick);
      const minPrice = getAdjustTradePrice(WrappedBigNumber.from(TickMath.getWadAtTick(min)), !!isInverse).toNumber();
      const maxPrice = getAdjustTradePrice(WrappedBigNumber.from(TickMath.getWadAtTick(max)), !!isInverse).toNumber();
      return {
        prices: isInverse ? [maxPrice, minPrice] : [minPrice, maxPrice],
        ticks: [min, max],
      };
    }
    return { prices: [], ticks: [] };
  }, [alpha, curTick, isInverse]);
}
export function useRemoveLiquidationSimulation(chainId: CHAIN_ID | undefined): IRemoveLiquiditySimulation | undefined {
  return useAppSelector(selectRemoveLiquiditySimulation(chainId));
}
export function useRemoveLiquidationSuccess(chainId: CHAIN_ID | undefined, earnType: EARN_TYPE): boolean {
  const isSuccess = useAppSelector(selectRemoveLiquiditySuccess(chainId));
  return useMemo(() => {
    return isSuccess && earnType === EARN_TYPE.REMOVE_LIQ;
  }, [earnType, isSuccess]);
}
export function useRemoveLiquidityFormState(chainId: CHAIN_ID | undefined): IRemoveLiquidityFormState | undefined {
  return useAppSelector(selectRemoveLiquidityFormStateByChainId(chainId));
}
export function useRemoveLiquidityFormStateStatus(chainId: CHAIN_ID | undefined): FETCHING_STATUS {
  return useAppSelector(selectRemoveLiquidityFormStatusByChainId(chainId));
}

export function useEarnFormType(chainId: CHAIN_ID | undefined): EARN_TYPE {
  return useAppSelector(selectEarnFormType(chainId));
}
export function useEarnPortFolioTab(): TABLE_TYPES {
  return useAppSelector(selectEarnPortfolioTab);
}

export function useWhiteListFetchingStatus(
  chainId: CHAIN_ID | undefined,
  quoteAddr: string | undefined,
): FETCHING_STATUS {
  const userAddr = useUserAddr();
  const { status } = useWhitelistConfig({ chainId, userAddr, quoteTokenAddress: quoteAddr });
  return useMemo(() => {
    if (!userAddr) return FETCHING_STATUS.DONE;
    switch (status) {
      case 'pending':
        return FETCHING_STATUS.FETCHING;
      case 'success':
      case 'error':
      default:
        return FETCHING_STATUS.DONE;
    }
  }, [status, userAddr]);
}

export function useIsInWhiteList(
  chainId: CHAIN_ID | undefined,
  userAddr: string | undefined,
  quote: TokenInfo | undefined,
): boolean {
  const { data, isFetched } = useWhitelistConfig({ chainId, userAddr, quoteTokenAddress: quote?.address });

  const whiteList = useMemo(() => {
    if (!userAddr) return [];
    return data?.isInWhiteList ? [userAddr] : [];
  }, [data, userAddr]);

  return useMemo(() => {
    if (!isFetched) {
      return true;
    }
    if (data?.allowUnauthorizedLPs) {
      return true;
    }
    if (userAddr && whiteList) {
      return !!whiteList.find((addr) => addr.toLowerCase() === userAddr.toLowerCase());
    }
    return true;
  }, [data, isFetched, userAddr, whiteList]);
}
export function usePairRangesMaxMin(
  chainId: CHAIN_ID | undefined,
  userAddr: string | undefined,
  pairId: string | undefined,
  currentPrice: number,
): number {
  const rangeList = useWrappedRangeList(chainId, userAddr, pairId);
  return useMemo(() => {
    if (rangeList?.length) {
      return _.reduce(
        rangeList,
        (ratio, cur) => {
          const leftRatio = getLeftRatio(cur.wrapAttribute('lowerPrice').toNumber(), currentPrice);
          const rightRatio = getRightRatio(cur.wrapAttribute('upperPrice').toNumber(), currentPrice);
          return Math.max(leftRatio, rightRatio, ratio);
        },
        Math.max(
          getLeftRatio(rangeList[0].wrapAttribute('lowerPrice').toNumber(), currentPrice),
          getRightRatio(rangeList[0].wrapAttribute('upperPrice').toNumber(), currentPrice),
        ),
      );
    }
    return 1;
  }, [currentPrice, rangeList]);
}
export function useChainPoolListFilters(chainId: number | undefined): ILiquidityFilters | undefined {
  const filters = useAppSelector(selectChainPoolFilters(chainId));
  return useMemo(() => filters, [filters]);
}
